import { create } from "zustand";
import { persist } from "zustand/middleware";
import { z } from "zod";

const LocalReviewEntrySchema = z.object({
  imageId: z.number(),
  labelId: z.number(),
  humanLabel: z.string(),
  reviewedAt: z.string(),
});

export type LocalReviewEntry = z.infer<typeof LocalReviewEntrySchema>;

const StorageSchema = z.record(z.string(), LocalReviewEntrySchema);

interface LocalReviewStore {
  overrides: Record<string, LocalReviewEntry>;
  setOverride: (imageId: number, labelId: number, humanLabel: string) => void;
  getOverride: (imageId: number) => LocalReviewEntry | undefined;
  removeOverride: (imageId: number) => void;
  clear: () => void;
}

export const useLocalReviewStore = create<LocalReviewStore>()(
  persist(
    (set, get) => ({
      overrides: {},

      setOverride: (imageId, labelId, humanLabel) =>
        set((state) => ({
          overrides: {
            ...state.overrides,
            [String(imageId)]: {
              imageId,
              labelId,
              humanLabel,
              reviewedAt: new Date().toISOString(),
            },
          },
        })),

      getOverride: (imageId) => get().overrides[String(imageId)],

      removeOverride: (imageId) =>
        set((state) => {
          const { [String(imageId)]: _, ...rest } = state.overrides;
          return { overrides: rest };
        }),

      clear: () => set({ overrides: {} }),
    }),
    {
      name: "ml-review-overrides",
      merge: (persisted, current) => {
        const parsed = StorageSchema.safeParse(
          (persisted as LocalReviewStore | undefined)?.overrides
        );
        return {
          ...current,
          overrides: parsed.success ? parsed.data : {},
        };
      },
    }
  )
);
